import {
  Component,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { TennisVideoService } from '../../gen2ai/tennis-video.service';
import { LibraryItem } from 'src/typings/library.interface';

@Component({
  selector: 'app-tennis-serve',
  templateUrl: './tennis-serve.component.html',
  styleUrls: ['./tennis-serve.component.scss']
})
export class TennisServeComponent implements OnInit, OnDestroy {
  @Output() insertIntoChat = new EventEmitter<string>();

  mediaForm: FormGroup;
  searchForm: FormGroup;

  /** All media items from service */
  private allItems: LibraryItem[] = [];

  /** View-model lists */
  pinnedItems: LibraryItem[] = [];
  otherItems: LibraryItem[] = [];

  labels: string[] = [];
  selectedLabel: string | null = null;
  showArchived = false;

  /** Vibrant tennis-inspired color palette */
  colorPalette: string[] = [
    '#ffffff', // default
    '#e8ff4f', // neon tennis ball
    '#00e5ff', // bright cyan
    '#ff6f00', // deep orange
    '#ff4081', // pink
    '#7c4dff', // violet
    '#00c853', // vivid green
    '#ffab00', // amber
    '#18ffff'  // electric aqua
  ];

  /** Upload / processing UI state */
  selectedFile: File | null = null;
  isSubmitting = false;

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private tennisService: TennisVideoService
  ) {
    this.mediaForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(180)]],
      description: ['', [Validators.maxLength(5000)]],
      labels: [''],
      color: ['#ffffff'],
      pinned: [false]
    });

    this.searchForm = this.fb.group({
      query: ['']
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Lifecycle                                                         */
  /* ------------------------------------------------------------------ */

  ngOnInit(): void {
    this.sub.add(
      this.tennisService.items$.subscribe(items => {
        this.allItems = items;
        this.refreshViewFromItems();
      })
    );

    this.sub.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.refreshViewFromItems();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /* ------------------------------------------------------------------ */
  /*  Creation / upload                                                 */
  /* ------------------------------------------------------------------ */

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.selectedFile = file ?? null;
  }

  async createItem(): Promise<void> {
    if (this.mediaForm.invalid || !this.selectedFile) {
      return;
    }

    const { title, description, labels, color, pinned } = this.mediaForm.value;

    const labelList = (labels || '')
      .split(',')
      .map((l: string) => l.trim())
      .filter((l: string) => l.length);

    const payload = {
      title: (title || '').trim(),
      description: (description || '').trim(),
      labels: labelList,
      color,
      pinned
    };

    if (!payload.title && !payload.description) {
      return;
    }

    try {
      this.isSubmitting = true;

      await this.tennisService.createItemWithUpload(payload, this.selectedFile);

      this.mediaForm.reset({
        title: '',
        description: '',
        labels: '',
        color: '#ffffff',
        pinned: false
      });
      this.selectedFile = null;
    } finally {
      this.isSubmitting = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  UI helpers                                                        */
  /* ------------------------------------------------------------------ */

  get filteredPinned(): LibraryItem[] {
    return this.applyFilters(this.pinnedItems);
  }

  get filteredOthers(): LibraryItem[] {
    return this.applyFilters(this.otherItems);
  }

  get selectedFileName(): string {
    return this.selectedFile ? this.selectedFile.name : 'Choose a serve video…';
  }

  private applyFilters(list: LibraryItem[]): LibraryItem[] {
    let res = [...list];

    if (!this.showArchived) {
      res = res.filter(n => !n.archived);
    }

    if (this.selectedLabel) {
      res = res.filter(n => n.labels?.includes(this.selectedLabel!));
    }

    const q: string = this.searchForm.get('query')!.value || '';
    if (q.trim()) {
      const needle = q.toLowerCase();
      res = res.filter(n =>
        (n.title || '').toLowerCase().includes(needle) ||
        (n.description || '').toLowerCase().includes(needle) ||
        (n.labels || []).some(l => l.toLowerCase().includes(needle)) ||
        (n.transcript || '').toLowerCase().includes(needle)
      );
    }

    return res;
  }

  private refreshViewFromItems(): void {
    this.pinnedItems = this.allItems.filter(n => n.pinned);
    this.otherItems = this.allItems.filter(n => !n.pinned);

    const labelSet = new Set<string>();
    this.allItems.forEach(n => (n.labels || []).forEach(l => labelSet.add(l)));
    this.labels = Array.from(labelSet).sort();
  }

  selectLabel(label: string | null): void {
    this.selectedLabel = label;
  }

  clearLabelFilter(): void {
    this.selectedLabel = null;
  }

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
  }

  async togglePinned(item: LibraryItem, pinned: boolean): Promise<void> {
    await this.tennisService.updateItem(item.id, { pinned });
  }

  async archive(item: LibraryItem): Promise<void> {
    await this.tennisService.updateItem(item.id, { archived: !item.archived });
  }

  async delete(item: LibraryItem): Promise<void> {
    if (confirm('Delete this serve video permanently?')) {
      await this.tennisService.deleteItem(item.id);
    }
  }

  async changeColor(item: LibraryItem, color: string): Promise<void> {
    item.color = color;
    await this.tennisService.updateItem(item.id, { color });
  }

  async reanalyze(item: LibraryItem): Promise<void> {
    await this.tennisService.triggerAnalysis(item.id);
  }

  openMedia(item: LibraryItem): void {
    if (!item.mediaUrl) return;
    window.open(item.mediaUrl, '_blank', 'noopener');
  }

  /** Build a tennis-specific summary to send into chat */
  useInChat(item: LibraryItem): void {
    const lines: string[] = [];

    if (item.title) lines.push(`Serve title: ${item.title}`);
    if (item.description) lines.push(`Notes: ${item.description}`);
    if (item.labels?.length) {
      lines.push(`Tags: ${item.labels.join(', ')}`);
    }

    const pose: any = item.pose || null;
    if (pose) {
      lines.push('');
      lines.push('Tennis serve analysis:');
      if (pose.category) {
        lines.push(`- Category: ${pose.category}`);
      }
      if (pose.racket_side) {
        lines.push(`- Racket side: ${pose.racket_side}`);
      }
      if (pose.trophy_angle_deg != null) {
        lines.push(
          `- Trophy elbow angle: ${pose.trophy_angle_deg.toFixed(1)}°` +
          (pose.trophy_timestamp_s != null
            ? ` at ${pose.trophy_timestamp_s.toFixed(2)} seconds`
            : '')
        );
      }
      if (Array.isArray(pose.samples) && pose.samples.length) {
        lines.push(
          `- Sampled elbow angles (every ~10 frames): ` +
          pose.samples
            .slice(0, 10)
            .map((s: any) =>
              `${(s.elbow_deg ?? 0).toFixed(1)}° @ ${(s.time_s ?? 0).toFixed(2)}s`
            )
            .join('; ')
        );
      }
    }

    if (item.transcript) {
      lines.push('');
      lines.push('Voice notes (excerpt):');
      lines.push(item.transcript);
    }

    this.insertIntoChat.emit(lines.join('\n'));
  }

  /* ------------------------------------------------------------------ */
  /*  Serve quality UI helpers                                          */
  /* ------------------------------------------------------------------ */

  getServeBadgeText(item: LibraryItem): string {
    const pose: any = item.pose || {};
    const category = (pose.category || '').toLowerCase();

    if (category === 'good') return 'Good Serve';
    if (category === 'needs_work' || category === 'bad') return 'Needs Work';
    return 'Unclassified';
  }

  getServeBadgeClass(item: LibraryItem): string {
    const pose: any = item.pose || {};
    const category = (pose.category || '').toLowerCase();

    if (category === 'good') return 'serve-badge--good';
    if (category === 'needs_work' || category === 'bad') return 'serve-badge--needs-work';
    return 'serve-badge--unknown';
  }

  /* ------------------------------------------------------------------ */
  /*  Drag & drop                                                       */
  /* ------------------------------------------------------------------ */

  dropPinned(ev: CdkDragDrop<LibraryItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.pinnedItems, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    void this.persistOrder();
  }

  dropOthers(ev: CdkDragDrop<LibraryItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.otherItems, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    void this.persistOrder();
  }

  private async persistOrder(): Promise<void> {
    const orderedIds = [
      ...this.pinnedItems.map(n => n.id),
      ...this.otherItems.map(n => n.id)
    ];
    await this.tennisService.reorderItems(orderedIds);
  }
}
